import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PackageCheck, PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import type { AccountDTO, LocationDTO } from '@invenco/accounts-interface';
import type { FulfillmentProfileDTO } from '@invenco/sales-interface/interfaces';
import { StockAvailabilityStrategy } from '@invenco/sales-interface/interfaces';

import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

function strategyLabel(strategies: StockAvailabilityStrategy[]): string {
  const key = strategies.join(',');
  switch (key) {
    case StockAvailabilityStrategy.AVAILABLE:
      return 'Available';
    case `${StockAvailabilityStrategy.AVAILABLE},${StockAvailabilityStrategy.PROJECTED}`:
      return 'Available then expected';
    case StockAvailabilityStrategy.PROJECTED:
      return 'Expected';
    default:
      return '—';
  }
}

const columnHelper = createColumnHelper<FulfillmentProfileDTO>();

function getColumns(
  accountsById: Record<string, AccountDTO>,
  locationsById: Record<string, LocationDTO>,
) {
  const accountLabel = (accountId: string) => accountsById[accountId]?.name ?? accountId;
  const locationLabel = (locationId: string) => locationsById[locationId]?.name ?? locationId;

  return [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('accountId', {
      header: 'Account',
      cell: ({ getValue }) => accountLabel(getValue()),
      sortingFn: (rowA, rowB) =>
        accountLabel(rowA.original.accountId).localeCompare(accountLabel(rowB.original.accountId)),
    }),
    columnHelper.accessor('fallbackLocationId', {
      header: 'Default location',
      cell: ({ getValue }) => locationLabel(getValue()),
      sortingFn: (rowA, rowB) =>
        locationLabel(rowA.original.fallbackLocationId).localeCompare(
          locationLabel(rowB.original.fallbackLocationId),
        ),
    }),
    columnHelper.accessor('stockAvailabilityStrategies', {
      header: 'Strategy',
      cell: ({ getValue }) => strategyLabel(getValue()),
      sortingFn: (rowA, rowB) =>
        strategyLabel(rowA.original.stockAvailabilityStrategies).localeCompare(
          strategyLabel(rowB.original.stockAvailabilityStrategies),
        ),
    }),
    columnHelper.accessor('updatedAt', {
      header: 'Updated',
      cell: ({ getValue }) => formatDateTime(getValue()),
    }),
  ];
}

const selectAccountsById = (accounts: AccountDTO[]): Record<string, AccountDTO> =>
  Object.fromEntries(
    accounts.flatMap((account) => (account.id ? [[account.id, account] as const] : [])),
  );

const selectLocationsById = (locations: LocationDTO[]): Record<string, LocationDTO> =>
  Object.fromEntries(locations.map((location) => [location.id, location] as const));

export function FulfillmentProfileList() {
  const { salesGateway, accountsGateway } = useGateways();

  const { data: accountsById } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
    select: selectAccountsById,
  });

  const { data: locationsById } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getLocations({ cursor, take }, { signal }),
      ),
    select: selectLocationsById,
  });

  const columns = useMemo(
    () => getColumns(accountsById ?? {}, locationsById ?? {}),
    [accountsById, locationsById],
  );

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
  });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'fulfillmentProfiles',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        salesGateway.getFulfillmentProfiles({ search: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <PackageCheck />
        </HeaderIcon>
        <HeaderTitle>Fulfillment profiles</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
